import { Empty, Skeleton, Table } from '@treams/treams-ui';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { HeartRateIcon } from '@treams/treams-icons';
import * as graphql from 'generated/graphql';

const Bar = styled(Box)<{ $color: string }>`
	height: 16px;
	background: ${(props) => props.theme.colors[props.$color]};
	color: white;
	text-align: center;
	line-height: 16px;
`;

function Distribution(props: { values: [number, number, number] }) {
	const sum = _.sum(props.values);

	return (
		<Flex style={{ borderRadius: 3, overflow: 'hidden', width: '100%' }}>
			<Bar width={((props.values[0] / sum) * 100).toFixed(2) + '%'} $color="red">
				{((props.values[0] / sum) * 100).toFixed(2) + '%'}
			</Bar>
			<Bar width={((props.values[1] / sum) * 100).toFixed(2) + '%'} $color="yellow">
				{((props.values[1] / sum) * 100).toFixed(2) + '%'}
			</Bar>
			<Bar width={((props.values[2] / sum) * 100).toFixed(2) + '%'} $color="green">
				{((props.values[2] / sum) * 100).toFixed(2) + '%'}
			</Bar>
		</Flex>
	);
}

export default function EngagementNpsAnalytics(props: { filters: graphql.AnalyticsFilters }) {
	const { t } = useTranslation();

	const query = graphql.useEngagementNpsAnalyticsQuery({ variables: { filters: props.filters } });

	return (
		<Table>
			<Table.Header>
				<Table.Cell basis={0.2}>{t('analytics.engagement.nps.header.department')}</Table.Cell>
				<Table.Cell basis={0.15}>{t('analytics.engagement.nps.header.employees')}</Table.Cell>
				<Table.Cell basis={0.5}>{t('analytics.engagement.nps.header.distribution')}</Table.Cell>
				<Table.Cell basis={0.15}>{t('analytics.engagement.nps.header.score')}</Table.Cell>
			</Table.Header>
			{query.data ? (
				query.data.workspaceAnalytics.engagementGroupsENPS.length === 0 &&
				typeof query.data.workspaceAnalytics.engagementENPS.score !== 'number' ? (
					<Skeleton.Empty
						contents={
							<Empty
								small
								icon={<HeartRateIcon />}
								title={t('analytics.engagement.nps.empty.title')}
								description={t('analytics.engagement.nps.empty.description')}
							/>
						}
					>
						<Skeleton.Repeat count={5}>
							<Table.Row>
								<Table.Cell.Skeleton basis={0.2} />
								<Table.Cell.Skeleton basis={0.15} />
								<Table.Cell.Skeleton basis={0.5} />
								<Table.Cell.Skeleton basis={0.15} />
							</Table.Row>
						</Skeleton.Repeat>
					</Skeleton.Empty>
				) : (
					<>
						{query.data.workspaceAnalytics.engagementGroupsENPS.map((record) => (
							<Table.Row key={record.group.id}>
								<Table.Cell basis={0.2}>{record.group.name}</Table.Cell>
								<Table.Cell basis={0.15}>
									{_.sum([record.detractors, record.passives, record.promoters])}
								</Table.Cell>
								<Table.Cell basis={0.5}>
									<Distribution
										values={[record.detractors ?? 0, record.passives ?? 0, record.promoters ?? 0]}
									/>
								</Table.Cell>
								<Table.Cell basis={0.15}>{record.score}</Table.Cell>
							</Table.Row>
						))}
						<Table.Footer>
							<Table.Cell basis={0.2}>{t('analytics.engagement.nps.workspace.total')}</Table.Cell>
							<Table.Cell basis={0.15}>
								{_.sum([
									query.data.workspaceAnalytics.engagementENPS.detractors,
									query.data.workspaceAnalytics.engagementENPS.passives,
									query.data.workspaceAnalytics.engagementENPS.promoters,
								])}
							</Table.Cell>
							<Table.Cell basis={0.5}>
								<Distribution
									values={[
										query.data.workspaceAnalytics.engagementENPS.detractors ?? 0,
										query.data.workspaceAnalytics.engagementENPS.passives ?? 0,
										query.data.workspaceAnalytics.engagementENPS.promoters ?? 0,
									]}
								/>
							</Table.Cell>
							<Table.Cell basis={0.15}>
								{query.data.workspaceAnalytics.engagementENPS.score}
							</Table.Cell>
						</Table.Footer>
					</>
				)
			) : (
				<Skeleton.Repeat count={5}>
					<Table.Row>
						<Table.Cell.Skeleton basis={0.2} />
						<Table.Cell.Skeleton basis={0.15} />
						<Table.Cell.Skeleton basis={0.5} />
						<Table.Cell.Skeleton basis={0.15} />
					</Table.Row>
				</Skeleton.Repeat>
			)}
		</Table>
	);
}
